/**
 * What a number FIELD reads and shows (KPR-1025): the parser and formatter
 * antd's InputNumber takes, for every number, decimal, integer and
 * measurement input, and the filter builder's number value.
 *
 * Reading. A typed fraction — "1 1/2", "1-1/2", "3/4", a fraction character
 * ("½", "1¾") — is read as the number it is, on every number input, whether
 * or not the element shows fractions: the shared reader
 * (src/utils/elementFormat/fraction.js, vendored from keepr-api with the
 * write path's copy, so the field and the API read one way). Before this,
 * antd's default parser dropped the space and the slash, so "1 1/2" became
 * 112. Anything with no fraction mark reads exactly as it did: grouping
 * commas dropped on a thousands element, and antd's own legacy clean-up
 * (`[^\w.-]` removed) everywhere else.
 *
 * A malformed fraction ("1//2", "1/0") reads as NOTHING (an empty field, so
 * nothing half-typed is saved as a different number), and
 * `fractionEntryProblem` gives the sentence the field shows under it.
 * Half-typed text ("1 1/") reads as nothing too while it is being typed; the
 * sentence is only shown once the field is left.
 *
 * Showing. With `fractions` on (a number, decimal or measurement element's
 * "Show as a fraction"), a value near a half, third, quarter or eighth reads
 * as that fraction whenever the field is not being typed in; any other value
 * reads as the element spells it.
 */
import { readFraction, formatFraction } from './elementFormat/fraction';
import { formatNumber } from './elementFormat/number';

const LEGACY_CLEAN = /[^\w.-]+/g;

/**
 * The text an InputNumber parses, for what was typed: a number's digits, or
 * '' for nothing (blank, or a malformed fraction). `thousands` drops grouping
 * commas, as the thousands field always did.
 */
export function parseNumberEntry( text, { thousands = false } = {} ) {
	const typed = text === null || text === undefined ? '' : String( text );
	const fraction = readFraction( typed );
	if ( fraction ) return fraction.error ? '' : String( fraction.value );
	return thousands ? typed.replace( /,/g, '' ) : typed.replace( LEGACY_CLEAN, '' );
}

/**
 * The sentence for typed text that looks like a fraction and is not one, or
 * null: '"1//2" is not a number: a fraction has one slash, like 3/4 or 1 1/2.'
 */
export function fractionEntryProblem( text ) {
	const typed = text === null || text === undefined ? '' : String( text ).trim();
	const fraction = readFraction( typed );
	if ( ! fraction || ! fraction.error ) return null;
	return `"${ typed.length > 40 ? `${ typed.slice( 0, 37 ) }...` : typed }" is not a number: ${ fraction.error }.`;
}

/**
 * The text a number field shows when it is not being typed in, or null when
 * the field should spell the value its own way (antd's precision, or the
 * caller's grouping). With `fractions`, a value near a fraction is that
 * fraction (grouped with `thousands`); otherwise the value at `precision`
 * decimals, grouped with `thousands`.
 */
export function displayNumberEntry( raw, { fractions = false, thousands = false, precision } = {} ) {
	if ( raw === null || raw === undefined || raw === '' ) return '';
	const num = Number( raw );
	if ( ! Number.isFinite( num ) ) return String( raw );
	if ( fractions ) {
		const fraction = formatFraction( num, { group: thousands } );
		if ( fraction !== null ) return fraction;
	}
	return formatNumber( raw, { decimals: precision, thousands } ) || String( raw );
}

/**
 * The parser / formatter pair for an antd InputNumber. `formatter` is left
 * out when the field needs neither fractions nor grouping, so antd keeps its
 * own spelling (precision) exactly as before.
 *
 * `value` is the number the field holds. antd re-reads the SHOWN text when the
 * field is left, and a value shown as its nearest fraction is not that
 * fraction (0.33 shows "1/3"; 0.12 shows "1/8"): read back, it would become
 * 0.333… or 0.125 though nobody typed anything. So text that is exactly what
 * the field shows for its value reads as that value, unchanged.
 */
export function numberEntryProps( { fractions = false, thousands = false, precision, value } = {} ) {
	const shown = ( fractions || thousands ) && typeof value === 'number' && Number.isFinite( value )
		? displayNumberEntry( value, { fractions, thousands, precision } )
		: null;
	const props = {
		parser: text => ( shown !== null && String( text ?? '' ).trim() === shown ? String( value ) : parseNumberEntry( text, { thousands } ) ),
	};
	if ( fractions || thousands ) {
		props.formatter = ( raw, info ) => ( info?.userTyping ? info.input : displayNumberEntry( raw, { fractions, thousands, precision } ) );
	}
	return props;
}
